import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { Icon, Dropdown, Menu, Popover, Button, Input, Tooltip, Checkbox, Spin } from 'antd';
import Moment from 'react-moment';
import moment from 'moment';
import { toBRL } from '~/components/UI/InputCurrency';
import { getLabel } from '~/helpers/paymentMethods';
import { PERMISSIONS } from '~/constants/permissions.constants';
import Download from '~/components/Components/Download';
import { Status } from '~/components/UI';
import Breakdown13Content from './Breakdown13Content';

var COL = {
    check: 50, status: 150, cod: 100, name: 400,
    apuration: 150, due: 150, method: 150, paid_at: 150,
    amount: 150, additionals: 150, paid: 200, interest: 200,
    discount: 200, amount13: 150, paid13: 150, balance: 200, actions: 150
};
var TABLE_W = Object.keys(COL).reduce(function(s, k) { return s + COL[k]; }, 0);

var thBase = {
    padding: '8px 12px', background: '#fafafa',
    borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8',
    borderRight: '1px solid #e8e8e8',
    fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis'
};
var tdBase = {
    padding: '8px 12px', background: '#fff',
    borderBottom: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
};

// Sticky styles for pinned columns
var stickyLeft = {
    position: 'sticky', left: 0, zIndex: 2
};
var stickyRight = {
    position: 'sticky', right: 0, zIndex: 2
};
var stickyLeftTh = {
    position: 'sticky', left: 0, zIndex: 3,
    borderLeft: '1px solid #e8e8e8'
};
var stickyRightTh = {
    position: 'sticky', right: 0, zIndex: 3,
    boxShadow: '-3px 0 6px rgba(0,0,0,0.06)'
};
var stickyLeftTd = {
    position: 'sticky', left: 0, zIndex: 2,
    borderLeft: '1px solid #e8e8e8'
};
var stickyRightTd = {
    position: 'sticky', right: 0, zIndex: 2,
    boxShadow: '-3px 0 6px rgba(0,0,0,0.06)'
};

function thS(w, extra) { return Object.assign({}, thBase, { width: w }, extra); }
function tdS(w, extra) { return Object.assign({}, tdBase, { width: w }, extra); }

var SORTERS = {
    name: function(a, b) {
        var ca = ((a.customer && a.customer.cod) || '').toString();
        var cb = ((b.customer && b.customer.cod) || '').toString();
        var c = ca.localeCompare(cb);
        if (c !== 0) return c;
        return ((a.customer && a.customer.name) || '').toString()
            .localeCompare(((b.customer && b.customer.name) || '').toString());
    },
    apuration_at: function(a, b) { return (a.apuration_at || '').localeCompare(b.apuration_at || ''); },
    due_at: function(a, b) { return (a.due_at || '').localeCompare(b.due_at || ''); },
    paid_at: function(a, b) { return (a.paid_at || '').localeCompare(b.paid_at || ''); },
    amount: function(a, b) { return (a.amount || 0) - (b.amount || 0); },
    additionals_amount: function(a, b) { return (a.additionals_amount || 0) - (b.additionals_amount || 0); },
    paid: function(a, b) { return (a.amount || 0) - (b.amount || 0); },
    interest: function(a, b) { return (a.interest || 0) - (b.interest || 0); },
    discount: function(a, b) { return (a.discount || 0) - (b.discount || 0); },
    amount13: function(a, b) { return (a.amount13 || 0) - (b.amount13 || 0); },
    paid13: function(a, b) { return (a.amount13 || 0) - (b.amount13 || 0); }
};

var TableList = React.memo(function TableList(props) {
    var isLoading = props.isLoading;
    var filteredData = props.filteredData;
    var data = props.data;
    var drawerPay = props.drawerPay;
    var isTwelfthOnboardingActive = props.isTwelfthOnboardingActive;
    var rowSelection = props.rowSelection;
    var user = props.user;
    var onAction = props.onAction;
    var onShowReconciliation = props.onShowReconciliation;
    var highlightedIds = props.highlightedIds;

    var sortState = React.useState('name');
    var sortField = sortState[0]; var setSortField = sortState[1];
    var orderState = React.useState('ascend');
    var sortOrder = orderState[0]; var setSortOrder = orderState[1];
    var statusState = React.useState(null);
    var statusFilter = statusState[0]; var setStatusFilter = statusState[1];
    var codState = React.useState('');
    var codFilter = codState[0]; var setCodFilter = codState[1];
    var codInputState = React.useState('');
    var codInput = codInputState[0]; var setCodInput = codInputState[1];
    var codOpenState = React.useState(false);
    var codOpen = codOpenState[0]; var setCodOpen = codOpenState[1];
    var empState = React.useState('');
    var empFilter = empState[0]; var setEmpFilter = empState[1];
    var empInputState = React.useState('');
    var empInput = empInputState[0]; var setEmpInput = empInputState[1];
    var empOpenState = React.useState(false);
    var empOpen = empOpenState[0]; var setEmpOpen = empOpenState[1];

    var processedData = React.useMemo(function() {
        var src = filteredData || [];
        var hasStatus = !!statusFilter;
        var hasCod = !!codFilter;
        var hasEmp = !!empFilter;
        var cf = hasCod ? codFilter.toLowerCase() : '';
        var ef = hasEmp ? empFilter.toLowerCase() : '';

        var result;
        if (!hasStatus && !hasCod && !hasEmp) {
            result = src;
        } else {
            result = [];
            for (var i = 0, len = src.length; i < len; i++) {
                var r = src[i];
                if (hasStatus && r.status !== statusFilter) continue;
                if (hasCod && !(r.cod && r.cod.toString().toLowerCase().indexOf(cf) !== -1)) continue;
                if (hasEmp) {
                    var nameMatch = r.customer && r.customer.name && r.customer.name.toString().toLowerCase().indexOf(ef) !== -1;
                    var codMatch = r.customer && r.customer.cod && r.customer.cod.toString().toLowerCase().indexOf(ef) !== -1;
                    if (!nameMatch && !codMatch) continue;
                }
                result.push(r);
            }
        }

        if (sortField && SORTERS[sortField]) {
            result = result.slice().sort(SORTERS[sortField]);
            if (sortOrder === 'descend') result.reverse();
        }
        return result;
    }, [filteredData, statusFilter, codFilter, empFilter, sortField, sortOrder]);

    function getFilename(record) {
        return record.customer.name + '_MENSALIDADE_' + moment(record.apuration_at).format('MM-YYYY') + '.pdf';
    }

    function toggleSort(field) {
        if (sortField === field) {
            if (sortOrder === 'ascend') { setSortOrder('descend'); }
            else { setSortField(null); setSortOrder(null); }
        } else {
            setSortField(field); setSortOrder('ascend');
        }
    }

    function sortIcon(field) {
        if (sortField !== field) return <Icon type="caret-down" style={{ marginLeft: 4, fontSize: 10, opacity: 0.3 }} />;
        return <Icon type={sortOrder === 'ascend' ? 'caret-up' : 'caret-down'} style={{ marginLeft: 4, fontSize: 10, color: '#1890ff' }} />;
    }

    var selectedKeys = rowSelection.selectedRowKeys;
    var selectedKeysSet = React.useMemo(function() { return new Set(selectedKeys); }, [selectedKeys]);
    var createdIdsSet = React.useMemo(function() {
        return new Set(highlightedIds && highlightedIds.created ? highlightedIds.created : []);
    }, [highlightedIds]);
    var allChecked = processedData.length > 0 && selectedKeys.length === processedData.length;
    var someChecked = selectedKeys.length > 0 && !allChecked;

    function onToggleAll(e) {
        if (e.target.checked) {
            rowSelection.onChange(processedData.map(function(r) { return r.id; }));
        } else {
            rowSelection.onChange([]);
        }
    }
    function onToggleRow(id) {
        var idx = selectedKeys.indexOf(id);
        if (idx === -1) { rowSelection.onChange(selectedKeys.concat([id])); }
        else { rowSelection.onChange(selectedKeys.filter(function(k) { return k !== id; })); }
    }

    function searchFilter(type) {
        var isOpen = type === 'cod' ? codOpen : empOpen;
        var setOpen = type === 'cod' ? setCodOpen : setEmpOpen;
        var input = type === 'cod' ? codInput : empInput;
        var setInput = type === 'cod' ? setCodInput : setEmpInput;
        var setFilter = type === 'cod' ? setCodFilter : setEmpFilter;
        var filterVal = type === 'cod' ? codFilter : empFilter;
        var label = type === 'cod' ? 'Cod' : 'Empresa';
        return (
            <Dropdown
                visible={isOpen}
                onVisibleChange={setOpen}
                trigger={['click']}
                overlay={
                    <div style={{ padding: 8, background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,.15)', borderRadius: 4 }}
                         onClick={function(e) { e.stopPropagation(); }}>
                        <Input
                            placeholder={'Filtra por ' + label}
                            value={input}
                            onChange={function(e) { setInput(e.target.value); }}
                            onPressEnter={function() { setFilter(input); setOpen(false); }}
                            style={{ width: 250, marginBottom: 8, display: 'block' }}
                        />
                        <Button type="primary" onClick={function() { setFilter(input); setOpen(false); }}
                            icon="search" size="small" style={{ width: 120, marginRight: 8 }}>
                            Pesquisar
                        </Button>
                        <Button onClick={function() { setFilter(''); setInput(''); setOpen(false); }}
                            size="small" style={{ width: 120 }}>
                            Resetar
                        </Button>
                    </div>
                }
            >
                <Icon type="search"
                    onClick={function(e) { e.stopPropagation(); }}
                    style={{ marginLeft: 4, color: filterVal ? '#1890ff' : '#bfbfbf', cursor: 'pointer', fontSize: 12 }}
                />
            </Dropdown>
        );
    }

    // Ref-based approach so tableComponents stays stable
    var dataRef = React.useRef({});
    dataRef.current = { processedData: processedData, highlightedIds: highlightedIds, createdIdsSet: createdIdsSet };

    var tableComponents = React.useMemo(function() {
        return {
            Table: function(p) {
                return <table {...p} style={Object.assign({}, p.style, {
                    borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: TABLE_W
                })} />;
            },
            TableRow: function(p) {
                var d = dataRef.current;
                var record = d.processedData[p['data-item-index']];
                var cls = 'row-clabs';
                if (record && d.highlightedIds) {
                    if (d.createdIdsSet.has(record.id)) {
                        cls += ' row-highlighted-created';
                    } else if (d.highlightedIds.updated && d.highlightedIds.updated[record.id]) {
                        cls += ' row-highlighted-updated';
                    }
                }
                return <tr {...p} className={cls + (p.className ? ' ' + p.className : '')} />;
            }
        };
    }, []);

    var fixedHeaderContent = React.useCallback(function() {
        return (
            <tr>
                <th style={thS(COL.check, Object.assign({ textAlign: 'center' }, stickyLeftTh))}>
                    <Checkbox checked={allChecked} indeterminate={someChecked} onChange={onToggleAll} />
                </th>
                <th style={thS(COL.status)}>
                    Status
                    <Dropdown trigger={['click']} overlay={
                        <Menu onClick={function(e) { setStatusFilter(e.key === 'all' ? null : e.key); }}>
                            <Menu.Item key="all">Todos</Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="Pago">Pago</Menu.Item>
                            <Menu.Item key="Pendente">Pendente</Menu.Item>
                            <Menu.Item key="Inadimplente">Inadimplente</Menu.Item>
                            <Menu.Item key="Ajuizado">Ajuizado</Menu.Item>
                        </Menu>
                    }>
                        <Icon type="filter" style={{ marginLeft: 4, color: statusFilter ? '#1890ff' : '#bfbfbf', cursor: 'pointer' }} />
                    </Dropdown>
                </th>
                <th style={thS(COL.cod)}>Cod {searchFilter('cod')}</th>
                <th style={thS(COL.name, { cursor: 'pointer' })} onClick={function() { toggleSort('name'); }}>
                    Empresa {sortIcon('name')} {searchFilter('empresa')}
                </th>
                <th style={thS(COL.apuration, { cursor: 'pointer' })} onClick={function() { toggleSort('apuration_at'); }}>
                    Competência {sortIcon('apuration_at')}
                </th>
                <th style={thS(COL.due, { cursor: 'pointer' })} onClick={function() { toggleSort('due_at'); }}>
                    Vencimento {sortIcon('due_at')}
                </th>
                <th style={thS(COL.method)}>Método Pgto</th>
                <th style={thS(COL.paid_at, { cursor: 'pointer' })} onClick={function() { toggleSort('paid_at'); }}>
                    Data de Pag. {sortIcon('paid_at')}
                </th>
                <th style={thS(COL.amount, { cursor: 'pointer' })} onClick={function() { toggleSort('amount'); }}>
                    Mensalidade {sortIcon('amount')}
                </th>
                <th style={thS(COL.additionals, { cursor: 'pointer' })} onClick={function() { toggleSort('additionals_amount'); }}>
                    Adicionais {sortIcon('additionals_amount')}
                </th>
                <th style={thS(COL.paid, { cursor: 'pointer' })} onClick={function() { toggleSort('paid'); }}>
                    Pago {sortIcon('paid')}
                </th>
                <th style={thS(COL.interest, { cursor: 'pointer' })} onClick={function() { toggleSort('interest'); }}>
                    Juros recebido {sortIcon('interest')}
                </th>
                <th style={thS(COL.discount, { cursor: 'pointer' })} onClick={function() { toggleSort('discount'); }}>
                    Desconto cedido {sortIcon('discount')}
                </th>
                <th style={thS(COL.amount13, { cursor: 'pointer' })} onClick={function() { toggleSort('amount13'); }}>
                    13º Salário {sortIcon('amount13')}
                </th>
                <th style={thS(COL.paid13, { cursor: 'pointer' })} onClick={function() { toggleSort('paid13'); }}>
                    13º Salário Pago {sortIcon('paid13')}
                </th>
                <th style={thS(COL.balance)}>Saldo</th>
                <th style={thS(COL.actions, Object.assign({ textAlign: 'center' }, stickyRightTh))}>Ações</th>
            </tr>
        );
    }, [allChecked, someChecked, sortField, sortOrder, statusFilter, codFilter, empFilter]);

    function itemContent(index, record) {
        var isSelected = selectedKeysSet.has(record.id);
        var isCreated = createdIdsSet.has(record.id);
        var updateChanges = highlightedIds && highlightedIds.updated && highlightedIds.updated[record.id];
        var showOnboarding = index === processedData.length - 1 && isTwelfthOnboardingActive && !drawerPay;

        var statusIcon = null;
        if (isCreated) {
            statusIcon = <Tooltip title="Nova mensalidade gerada"><Icon type="plus-circle" style={{ color: '#52c41a', marginLeft: 6 }} /></Tooltip>;
        } else if (updateChanges) {
            var lines = [];
            if (updateChanges.old_amount !== updateChanges.amount) lines.push('Mensalidade: ' + toBRL(updateChanges.old_amount) + ' → ' + toBRL(updateChanges.amount));
            if (updateChanges.old_amount13 !== updateChanges.amount13) lines.push('13º: ' + toBRL(updateChanges.old_amount13) + ' → ' + toBRL(updateChanges.amount13));
            statusIcon = <Tooltip title={lines.join(' | ')}><Icon type="edit" style={{ color: '#1890ff', marginLeft: 6 }} /></Tooltip>;
        }

        return (
            <>
                <td style={tdS(COL.check, Object.assign({ textAlign: 'center' }, stickyLeftTd))}>
                    <Checkbox checked={isSelected} onChange={function() { onToggleRow(record.id); }} />
                </td>
                <td style={tdS(COL.status)}>
                    <Status
                        value={record.status}
                        icon={record.justification === 'Pago via conciliação bancária' ? (
                            <Tooltip title="Ver detalhes da conciliação">
                                <Icon
                                    type="bank"
                                    style={{ fontSize: 11, marginLeft: 2, cursor: 'pointer' }}
                                    onClick={function(e) { e.stopPropagation(); if (onShowReconciliation) onShowReconciliation(record.id); }}
                                />
                            </Tooltip>
                        ) : null}
                    />
                    {statusIcon}
                </td>
                <td style={tdS(COL.cod)}>{record.cod}</td>
                <td style={tdS(COL.name)}>
                    {record.customer && record.customer.name ? (
                        <span>{record.customer.cod} - {record.customer.name.length > 35 ? record.customer.name.slice(0, 35) + '...' : record.customer.name}</span>
                    ) : ''}
                </td>
                <td style={tdS(COL.apuration, { textAlign: 'center' })}><Moment format="MM/YYYY">{record.apuration_at}</Moment></td>
                <td style={tdS(COL.due, { textAlign: 'center' })}><Moment format="DD/MM/YYYY">{record.due_at}</Moment></td>
                <td style={tdS(COL.method)}>
                    {record.method ? getLabel(record.method).name : (
                        record.justification === 'Pago via conciliação bancária' ? (
                            <Tooltip title="Ver detalhes da conciliação">
                                <span
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#52c41a', fontSize: 12, cursor: 'pointer' }}
                                    onClick={function(e) { e.stopPropagation(); if (onShowReconciliation) onShowReconciliation(record.id); }}
                                >
                                    <Icon type="bank" />Conciliação
                                </span>
                            </Tooltip>
                        ) : null
                    )}
                </td>
                <td style={tdS(COL.paid_at, { textAlign: 'center' })}>{record.paid_at && <Moment format="DD/MM/YYYY">{record.paid_at}</Moment>}</td>
                <td style={tdS(COL.amount)}>{toBRL(record.amount)}</td>
                <td style={tdS(COL.additionals)}>{toBRL(record.additionals_amount)}</td>
                <td style={tdS(COL.paid)}>{record.paid ? toBRL(record.paid) : '0,00'}</td>
                <td style={tdS(COL.interest)}>{record.interest ? toBRL(record.interest) : '0,00'}</td>
                <td style={tdS(COL.discount)}>{record.discount ? toBRL(record.discount) : '0,00'}</td>
                <td style={tdS(COL.amount13)}>
                    {toBRL(record.amount13)}
                    {record.amount13_breakdown ? (
                        <Popover
                            placement="left"
                            content={<Breakdown13Content breakdown={record.amount13_breakdown} stored={record.amount13} />}
                            trigger="hover"
                            mouseEnterDelay={0.2}
                        >
                            <Icon
                                type={record.amount13_breakdown.is_partial ? 'warning' : 'info-circle'}
                                style={{
                                    marginLeft: 6,
                                    color: record.amount13_breakdown.is_partial ? '#faad14' : '#1890ff',
                                    cursor: 'help',
                                    fontSize: 12
                                }}
                                onClick={function(e) { e.stopPropagation(); }}
                            />
                        </Popover>
                    ) : null}
                </td>
                <td style={tdS(COL.paid13)}>{toBRL(record.paid13)}</td>
                <td style={tdS(COL.balance)}>
                    {record.paid
                        ? toBRL((parseFloat(record.amount) + parseFloat(record.additionals_amount)) - parseFloat(record.paid))
                        : toBRL(parseFloat(record.amount) + parseFloat(record.additionals_amount))}
                </td>
                <td style={tdS(COL.actions, Object.assign({ textAlign: 'center' }, stickyRightTd))}>
                    <span onClick={function(e) { e.stopPropagation(); }}>
                        <Dropdown overlay={
                            <Menu onClick={function(e) { onAction(e, record); }}>
                                {record.paid_at === null ?
                                    !user.permissions.includes(PERMISSIONS.financial.monthlyPayments.edit) ?
                                        <Menu.Item key="pay" disabled={showOnboarding}><Icon type="check" /> Marcar como Pago</Menu.Item>
                                    :
                                        <Menu.Item key="pay"><Icon type="edit" /> Editar</Menu.Item>
                                :
                                    <Menu.Item key="pay" disabled={showOnboarding}><Icon type="edit" /> Alterar</Menu.Item>
                                }
                                {record.status !== 'Pago' && record.status !== 'Inadimplente' && (
                                    <Menu.Item key="inadimplente" disabled={showOnboarding}><Icon type="exclamation-circle" /> Marcar como inadimplente</Menu.Item>
                                )}
                                {record.status !== 'Pago' && record.status !== 'Ajuizado' && (
                                    <Menu.Item key="ajuizado" disabled={showOnboarding}><Icon type="audit" /> Marcar como ajuizado</Menu.Item>
                                )}
                                <Menu.Item disabled={showOnboarding}>
                                    <Download url={'/monthly/payments/print/' + record.id} filename={getFilename(record)}>
                                        <div><Icon style={{ minWidth: 12, marginRight: 8, fontSize: 12 }} type="printer" /> Imprimir</div>
                                    </Download>
                                </Menu.Item>
                                <Menu.Divider />
                                {user.permissions.includes(PERMISSIONS.financial.monthlyPayments.delete) && (
                                    <Menu.Item key="delete" disabled={showOnboarding}><Icon type="delete" /> Deletar</Menu.Item>
                                )}
                            </Menu>
                        } trigger={['click']}>
                            <Popover placement="bottomRight" content="Clique aqui, edite esta mensalidade e defina como quitado" visible={showOnboarding}>
                                <a className="ant-dropdown-link" href="#">Ações <Icon type="down" /></a>
                            </Popover>
                        </Dropdown>
                    </span>
                </td>
            </>
        );
    }

    return (
        <Spin spinning={isLoading}>
            <TableVirtuoso
                style={{ height: window.innerHeight - 175, border: '1px solid #e8e8e8' }}
                data={processedData}
                components={tableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={itemContent}
                overscan={20}
            />
            <div style={{ padding: '8px 16px', background: '#fafafa', border: '1px solid #e8e8e8', borderTop: 'none', fontSize: 13 }}>
                Total: {processedData.length} mensalidade(s)
            </div>
        </Spin>
    );
});

export default TableList;
